import { EventEmitter } from 'events';
import React, { Component, createContext, ReactNode } from 'react';

// The service
export enum ModalPriority {
  HIGHER = 100,
  NORMAL = 50,
  LOWER = 25,
}

export const MODAL_CHANGED_EVENT = 'currentModalChanged';

export class ModalCoordinator extends EventEmitter {
  private readonly modals: Array<{ component: any, priority: ModalPriority }> = [];
  private currentModal: any = undefined;

  public addModal(component: any, priority = ModalPriority.NORMAL): boolean {
    this.modals.splice(this.getInsertPosition(priority), 0, { component, priority });
    this.updateCurrentModal();
    return component === this.currentModal;
  }

  public removeModal(component: any): void {
    const componentPosition = this.modals.findIndex(({ component: existingComponent }) =>
      existingComponent === component);

    if (componentPosition !== -1) {
      this.modals.splice(componentPosition, 1);
      this.updateCurrentModal();
    }
  }

  private getInsertPosition(priority: ModalPriority): number {
    const insertPos = this.modals.findIndex(({ priority: existingPriority }) =>
      existingPriority < priority);

    return insertPos === -1 ? this.modals.length : insertPos;
  }

  private updateCurrentModal(): void {
    const newCurrentModal = this.modals[0] && this.modals[0].component;

    if (newCurrentModal !== this.currentModal) {
      this.currentModal = newCurrentModal;
      this.emit(MODAL_CHANGED_EVENT, this.currentModal);
    }
  }
}

// The context

export const {
  Consumer: GetModalCoordinator,
  Provider: SetModalCoordinator,
} = createContext<ModalCoordinator | undefined>(undefined);

export const {
  Consumer: GetCurrentModal,
  Provider: SetCurrentModal,
} = createContext<any>(undefined);

// The default context provider

interface IWithModalCoordinatorState {
  currentModal: any;
}

export class WithModalCoordinator extends Component<unknown, IWithModalCoordinatorState> {
  private readonly modalCoordinator: ModalCoordinator = new ModalCoordinator();

  public constructor(initialProps: unknown) {
    super(initialProps);

    this.state = {
      currentModal: undefined,
    };

    this.modalCoordinator.addListener(MODAL_CHANGED_EVENT, this.currentModalChanged);
  }

  public componentWillUnmount() {
    this.modalCoordinator.removeListener(MODAL_CHANGED_EVENT, this.currentModalChanged);
  }

  public render() {
    return (
      <SetModalCoordinator value={this.modalCoordinator}>
        <SetCurrentModal value={this.state.currentModal}>
          {this.props.children}
        </SetCurrentModal>
      </SetModalCoordinator>
    );
  }

  private readonly currentModalChanged = (currentModal: unknown) => {
    this.setState({ currentModal });
  };
}

// The consumer

interface IRenderModalIfCurrentProps {
  modalCoordinator: ModalCoordinator;
  priority?: ModalPriority;
  children?: ReactNode;
}

interface IRenderModalIfCurrentState {
  current: boolean;
}

export class RenderModalIfCurrent extends Component<
  IRenderModalIfCurrentProps, IRenderModalIfCurrentState
> {
  public constructor(initialProps: IRenderModalIfCurrentProps) {
    super(initialProps);

    const { modalCoordinator, priority } = initialProps;

    modalCoordinator.addModal(this, priority);
  }

  public componentWillUnmount() {
    this.props.modalCoordinator.removeModal(this);
  }

  public render() {
    return (
      <GetCurrentModal>{(currentModal) => (
        currentModal === this ? this.props.children : null
      )}</GetCurrentModal>
    );
  }
}
